/**
 * @file OptimizedImage.tsx
 * @description Wrapper component for optimized images with WebP fallback
 *
 * Usage:
 * <OptimizedImage
 *   src="/images/promo/atc-MAD-wall.jpg"
 *   alt="Comedy venue"
 *   width={1200}
 *   height={800}
 *   priority
 * />
 */

import Image, { ImageProps } from "next/image";
import React from "react";

interface OptimizedImageProps extends Omit<ImageProps, "src"> {
  /** JPEG/PNG fallback path (e.g., "/images/promo/photo.jpg") */
  src: string;
}

/**
 * OptimizedImage component
 *
 * Automatically serves WebP to browsers that support it, falls back to JPEG/PNG
 * - Expects both .webp and .jpg/.png versions to exist
 * - Uses HTML <picture> element for format negotiation
 * - Compatible with Next.js Image for optimization
 * - Supports refs via forwardRef
 *
 * @param src - Path to the fallback (e.g., "/images/promo/photo.jpg")
 * @param props - Standard Next.js Image props
 */
export const OptimizedImage = React.forwardRef<HTMLImageElement, OptimizedImageProps>(
  function OptimizedImage({ src, alt, ...props }, ref) {
    // Convert .jpg/.jpeg/.png to .webp for modern browsers
    const webpSrc = src.replace(/\.(jpg|jpeg|png)$/i, ".webp");

    // Determine the MIME type for the fallback source
    const ext = src.match(/\.(jpg|jpeg|png)$/i)?.[1].toLowerCase();
    const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg';

    return (
      <picture>
        {/* Modern browsers (WebP support) */}
        <source srcSet={webpSrc} type="image/webp" />

        {/* Fallback for older browsers */}
        <source srcSet={src} type={mimeType} />

        {/* Image component for Next.js optimization + fallback */}
        <Image ref={ref} src={src} alt={alt} {...props} />
      </picture>
    );
  }
);

export default OptimizedImage;
